import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSubscriptionPayment } from '@/hooks/useSubscriptionPayment';
import { useRouter, useSearchParams } from 'next/navigation';
import { Plan } from '@/lib/api';
import { auth } from '@/lib/firebase';
import { URL_PARAMS, buildUrlParams, resolveUtmTrackingParams, sanitizeReturnPath } from '@/lib/url-utils';
import { usePlayStoreRedirect } from '@/hooks/usePlayStoreRedirect';
import PaymentStatusUI from './PaymentStatusUI';
import { PAYMENT_METHODS, getAuthorizationAmountForPaymentMethod, type PaymentApp, type PaymentMethod } from '@/constants/payment';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { toast } from 'sonner';
interface Props {
  open: boolean;
  onClose: () => void;
  plan: Plan | null;
  planIndex?: number;
  user?: {
    _id?: string;
    fullName?: string;
    email?: string;
    phoneNumber?: string;
  } | null;
  amount?: number;
  onPaymentStart?: (app: PaymentMethod) => void;
  autoStart?: boolean;
  defaultApp?: PaymentMethod;
  couponCode?: string | null;
  onCancelled?: () => void;
}
const UPI_ID_PATTERN = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
const resolvePaymentApp = (app?: PaymentMethod): PaymentApp => {
  if (app === 'GPAY' || app === 'PAYTM' || app === 'PHONEPE') {
    return app;
  }
  return 'PHONEPE';
};
export default function PaymentModal(t0) {
  const $ = _c(140);
  const {
    open,
    onClose,
    plan,
    planIndex,
    user,
    onPaymentStart,
    autoStart: t1,
    defaultApp: t2,
    couponCode,
    onCancelled
  } = t0;
  const autoStart = t1 === undefined ? false : t1;
  const defaultApp = t2 === undefined ? "PHONEPE" : t2;
  let t3;
  if ($[0] !== defaultApp) {
    t3 = resolvePaymentApp(defaultApp);
    $[0] = defaultApp;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [selectedApp, setSelectedApp] = useState(t3);
  const [showUpiId, setShowUpiId] = useState(false);
  const [upiId, setUpiId] = useState("");
  const showSelection = !autoStart;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  let t4;
  let t5;
  if ($[2] !== defaultApp) {
    t4 = () => {
      setSelectedApp(resolvePaymentApp(defaultApp));
    };
    t5 = [defaultApp];
    $[2] = defaultApp;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const router = useRouter();
  const searchParams = useSearchParams();
  let t6;
  if ($[5] !== searchParams) {
    t6 = resolveUtmTrackingParams(searchParams);
    $[5] = searchParams;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const utmTrackingParams = t6;
  const {
    startPayment,
    cancel,
    isProcessing,
    timer,
    status,
    subscriptionId,
    failureReason,
    responseCode
  } = useSubscriptionPayment();
  const {
    redirectToPlayStore
  } = usePlayStoreRedirect(searchParams);
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const unsubscribe = auth?.onAuthStateChanged(user_0 => {
        setIsLoggedIn(!!user_0);
      });
      return () => unsubscribe?.();
    };
    t8 = [];
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== defaultApp || $[10] !== open || $[11] !== showSelection || $[12] !== showUpiId || $[13] !== status || $[14] !== user?._id || $[15] !== utmTrackingParams) {
    t9 = () => {
      if (open && showSelection && status === "idle") {
        trackAnalyticsEvent("WEB_BOTTOM_SHEET_VIEWED", {
          user_id: user?._id || "guest",
          payment_method: defaultApp,
          provider: showUpiId ? "cashfree" : "phonepe",
          ...utmTrackingParams
        });
      }
    };
    $[9] = defaultApp;
    $[10] = open;
    $[11] = showSelection;
    $[12] = showUpiId;
    $[13] = status;
    $[14] = user?._id;
    $[15] = utmTrackingParams;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = user?._id;
  let t11;
  if ($[17] !== defaultApp || $[18] !== open || $[19] !== showSelection || $[20] !== showUpiId || $[21] !== status || $[22] !== t10 || $[23] !== utmTrackingParams) {
    t11 = [open, showSelection, status, t10, defaultApp, showUpiId, utmTrackingParams];
    $[17] = defaultApp;
    $[18] = open;
    $[19] = showSelection;
    $[20] = showUpiId;
    $[21] = status;
    $[22] = t10;
    $[23] = utmTrackingParams;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  useEffect(t9, t11);
  let t12;
  if ($[25] !== failureReason || $[26] !== responseCode || $[27] !== selectedApp || $[28] !== showUpiId || $[29] !== status || $[30] !== upiId || $[31] !== user?._id || $[32] !== utmTrackingParams) {
    t12 = () => {
      if (status === "failure") {
        trackAnalyticsEvent("WEB_PAYMENT_FAILURE_SCREEN_VIEWED", {
          user_id: user?._id || "guest",
          payment_method: showUpiId ? upiId || "VPA" : selectedApp,
          provider: showUpiId ? "cashfree" : "phonepe",
          failure_reason: failureReason || "TIMEOUT_OR_FAILURE",
          response_code: responseCode || "400",
          ...utmTrackingParams
        });
      }
    };
    $[25] = failureReason;
    $[26] = responseCode;
    $[27] = selectedApp;
    $[28] = showUpiId;
    $[29] = status;
    $[30] = upiId;
    $[31] = user?._id;
    $[32] = utmTrackingParams;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = user?._id;
  let t14;
  if ($[34] !== failureReason || $[35] !== responseCode || $[36] !== selectedApp || $[37] !== showUpiId || $[38] !== status || $[39] !== t13 || $[40] !== upiId || $[41] !== utmTrackingParams) {
    t14 = [status, failureReason, responseCode, selectedApp, showUpiId, upiId, t13, utmTrackingParams];
    $[34] = failureReason;
    $[35] = responseCode;
    $[36] = selectedApp;
    $[37] = showUpiId;
    $[38] = status;
    $[39] = t13;
    $[40] = upiId;
    $[41] = utmTrackingParams;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  useEffect(t12, t14);
  let t15;
  if ($[43] !== onClose || $[44] !== router || $[45] !== searchParams || $[46] !== showUpiId || $[47] !== status || $[48] !== user?._id || $[49] !== utmTrackingParams) {
    t15 = () => {
      if (status === "pending") {
        trackAnalyticsEvent("WEB_POLLING_CANCELLED", {
          user_id: user?._id || "guest",
          previous_failure_stage: "pending",
          provider: showUpiId ? "cashfree" : "phonepe",
          ...utmTrackingParams
        });
      }
      onClose();
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        const params = new URLSearchParams(searchParams.toString());
        const queryString = params.toString();
        const rawReturnPath = searchParams.get(URL_PARAMS.RETURN_PATH);
        const returnPath = rawReturnPath ? sanitizeReturnPath(rawReturnPath) : window.location.pathname;
        router.replace(queryString ? `${returnPath}?${queryString}` : returnPath);
      }
    };
    $[43] = onClose;
    $[44] = router;
    $[45] = searchParams;
    $[46] = showUpiId;
    $[47] = status;
    $[48] = user?._id;
    $[49] = utmTrackingParams;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const handleClose = t15;
  let t16;
  if ($[51] !== couponCode || $[52] !== onClose || $[53] !== plan || $[54] !== redirectToPlayStore || $[55] !== startPayment || $[56] !== subscriptionId || $[57] !== user || $[58] !== utmTrackingParams) {
    t16 = async opts => {
      await startPayment({
        plan,
        user: user || undefined,
        method: opts.upiId ? "VPA" : "APP",
        upiId: opts.upiId || undefined,
        ...(opts.upiApp ? {
          upiApp: opts.upiApp
        } : {}),
        ...(couponCode && {
          couponCode
        }),
        sourcePath: sessionStorage.getItem("landingPath") || undefined,
        utmTrackingParams,
        onSuccess: () => {
          trackAnalyticsEvent("WEB_PAYMENT_SUCCESS_SCREEN_VIEWED", {
            user_id: user?._id || "guest",
            subscriptionId: subscriptionId || "",
            payment_method: opts.upiApp || opts.upiId || "VPA",
            provider: opts.upiId ? "cashfree" : "phonepe",
            success: true,
            ...utmTrackingParams
          });
          onClose();
          redirectToPlayStore();
        },
        onError: _temp
      });
    };
    $[51] = couponCode;
    $[52] = onClose;
    $[53] = plan;
    $[54] = redirectToPlayStore;
    $[55] = startPayment;
    $[56] = subscriptionId;
    $[57] = user;
    $[58] = utmTrackingParams;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  const handleStart = t16;
  let t17;
  let t18;
  if ($[60] !== autoStart || $[61] !== defaultApp || $[62] !== handleStart || $[63] !== isProcessing || $[64] !== open || $[65] !== plan || $[66] !== status) {
    t17 = () => {
      if (open && autoStart && plan && !isProcessing && status === "idle") {
        handleStart({
          upiApp: resolvePaymentApp(defaultApp)
        });
      }
    };
    t18 = [autoStart, defaultApp, handleStart, isProcessing, open, plan, status];
    $[60] = autoStart;
    $[61] = defaultApp;
    $[62] = handleStart;
    $[63] = isProcessing;
    $[64] = open;
    $[65] = plan;
    $[66] = status;
    $[67] = t17;
    $[68] = t18;
  } else {
    t17 = $[67];
    t18 = $[68];
  }
  useEffect(t17, t18);
  let t19;
  if ($[69] !== couponCode || $[70] !== handleStart || $[71] !== isLoggedIn || $[72] !== onPaymentStart || $[73] !== planIndex || $[74] !== router || $[75] !== searchParams || $[76] !== selectedApp || $[77] !== showUpiId || $[78] !== upiId || $[79] !== user?._id || $[80] !== utmTrackingParams) {
    t19 = async () => {
      const trimmedUpiId = upiId.trim();
      trackAnalyticsEvent("WEB_BOTTOM_SHEET_BTN_CLICKED", {
        user_id: user?._id || "guest",
        payment_method: showUpiId ? trimmedUpiId || "VPA" : selectedApp,
        provider: showUpiId ? "cashfree" : "phonepe",
        amount: getAuthorizationAmountForPaymentMethod(showUpiId ? trimmedUpiId || "VPA" : selectedApp),
        ...utmTrackingParams
      });
      trackAnalyticsEvent("WEB_SUBSCRIBE_BUTTON_CLICKED", {
        user_id: user?._id || "guest",
        payment_method: showUpiId ? trimmedUpiId || "VPA" : selectedApp,
        provider: showUpiId ? "cashfree" : "phonepe",
        amount: getAuthorizationAmountForPaymentMethod(showUpiId ? trimmedUpiId || "VPA" : selectedApp),
        ...utmTrackingParams
      });
      if (showUpiId && !UPI_ID_PATTERN.test(trimmedUpiId)) {
        toast.error("Invalid UPI ID");
        return;
      }
      if (!isLoggedIn) {
        const additionalParams = {
          [URL_PARAMS.PLAN]: String(planIndex ?? 0),
          [URL_PARAMS.AUTO]: "1",
          [URL_PARAMS.PAYMENT_APP]: showUpiId ? trimmedUpiId : selectedApp,
          [URL_PARAMS.RETURN_PATH]: window.location.pathname
        };
        if (couponCode) {
          additionalParams[URL_PARAMS.COUPON_CODE] = couponCode;
        }
        const params_0 = buildUrlParams(searchParams, [URL_PARAMS.SHOW_ID, URL_PARAMS.EPISODE_NUMBER, URL_PARAMS.LINK_TOKEN], additionalParams);
        router.push(`/auth/login?${params_0}`);
        return;
      }
      if (showUpiId) {
        if (onPaymentStart) {
          onPaymentStart("DEFAULT");
        }
        await handleStart({
          upiId: trimmedUpiId
        });
      } else {
        if (onPaymentStart) {
          onPaymentStart(selectedApp);
        }
        await handleStart({
          upiApp: selectedApp
        });
      }
    };
    $[69] = couponCode;
    $[70] = handleStart;
    $[71] = isLoggedIn;
    $[72] = onPaymentStart;
    $[73] = planIndex;
    $[74] = router;
    $[75] = searchParams;
    $[76] = selectedApp;
    $[77] = showUpiId;
    $[78] = upiId;
    $[79] = user?._id;
    $[80] = utmTrackingParams;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  const handleProceedToPay = t19;
  if (!open || !plan) {
    return null;
  }
  if (status === "pending" && subscriptionId) {
    const t20 = showUpiId ? upiId || "DEFAULT" : selectedApp;
    const t21 = status === "pending";
    let t22;
    if ($[82] !== cancel || $[83] !== handleClose || $[84] !== onCancelled || $[85] !== t20 || $[86] !== t21 || $[87] !== timer) {
      t22 = <PaymentStatusUI status="pending" timer={timer} appName={t20} cancelPayment={cancel} onNavigate={handleClose} isPending={t21} onCancelled={onCancelled} />;
      $[82] = cancel;
      $[83] = handleClose;
      $[84] = onCancelled;
      $[85] = t20;
      $[86] = t21;
      $[87] = timer;
      $[88] = t22;
    } else {
      t22 = $[88];
    }
    return t22;
  }
  if (status === "failure") {
    const t20 = showUpiId ? upiId || "DEFAULT" : selectedApp;
    let t21;
    if ($[89] !== cancel || $[90] !== handleClose || $[91] !== onCancelled || $[92] !== t20) {
      t21 = <PaymentStatusUI status="failure" appName={t20} cancelPayment={cancel} onNavigate={handleClose} isPending={false} onCancelled={onCancelled} />;
      $[89] = cancel;
      $[90] = handleClose;
      $[91] = onCancelled;
      $[92] = t20;
      $[93] = t21;
    } else {
      t21 = $[93];
    }
    return t21;
  }
  if (!showSelection) {
    return null;
  }
  let t20;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h3 className="text-base font-semibold">Choose a payment method</h3>;
    $[94] = t20;
  } else {
    t20 = $[94];
  }
  let t21;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <X size={20} />;
    $[95] = t21;
  } else {
    t21 = $[95];
  }
  let t22;
  if ($[96] !== handleClose) {
    t22 = <div className="flex justify-between items-center p-4 border-b border-tertiary-grey">{t20}<button onClick={handleClose} className="text-secondary-grey hover:text-white transition-colors">{t21}</button></div>;
    $[96] = handleClose;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  let t23;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="flex justify-between items-center mb-3"><span className="text-sm font-medium">UPI</span></div>;
    $[98] = t23;
  } else {
    t23 = $[98];
  }
  let t24;
  if ($[99] !== onPaymentStart || $[100] !== selectedApp || $[101] !== showUpiId || $[102] !== user?._id || $[103] !== utmTrackingParams) {
    t24 = PAYMENT_METHODS.map(t25 => {
      const {
        id,
        label,
        icon
      } = t25;
      return <button key={id} onClick={() => {
        setSelectedApp(id);
        setShowUpiId(false);
        if (onPaymentStart) {
          onPaymentStart(id);
        }
        trackAnalyticsEvent("WEB_PAYMENT_METHOD_SELECTED", {
          user_id: user?._id || "guest",
          payment_method: id,
          provider: "phonepe",
          ...utmTrackingParams
        });
      }} className="w-full flex items-center justify-between p-3 rounded-lg bg-deep-blue hover:bg-tertiary-grey transition"><div className="flex items-center gap-3"><div className="p-1.5 bg-white rounded-full"><img src={icon} alt={label} className="w-6 h-6" /></div><span className="text-sm">{label}</span></div><div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedApp === id && !showUpiId ? "border-primary bg-primary" : "border-secondary-grey"}`}>{selectedApp === id && !showUpiId && <div className="w-2 h-2 rounded-full bg-white" />}</div></button>;
    });
    $[99] = onPaymentStart;
    $[100] = selectedApp;
    $[101] = showUpiId;
    $[102] = user?._id;
    $[103] = utmTrackingParams;
    $[104] = t24;
  } else {
    t24 = $[104];
  }
  let t25;
  if ($[105] !== t24) {
    t25 = <div className="space-y-2 mb-3">{t24}</div>;
    $[105] = t24;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  let t26;
  if ($[107] !== onPaymentStart || $[108] !== showUpiId) {
    t26 = () => {
      const next = !showUpiId;
      setShowUpiId(next);
      if (onPaymentStart && next) {
        onPaymentStart("DEFAULT");
      }
    };
    $[107] = onPaymentStart;
    $[108] = showUpiId;
    $[109] = t26;
  } else {
    t26 = $[109];
  }
  let t27;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="flex items-center gap-3"><div className="w-6 h-6 rounded-full bg-white flex items-center justify-center p-1"><img src="/upi-144.svg" alt="UPI" className="w-full h-full" /></div><span className="text-sm text-primary">Add UPI ID</span></div>;
    $[110] = t27;
  } else {
    t27 = $[110];
  }
  let t28;
  if ($[111] !== showUpiId) {
    t28 = showUpiId ? <ChevronUp size={16} className="text-primary" /> : <ChevronDown size={16} className="text-primary" />;
    $[111] = showUpiId;
    $[112] = t28;
  } else {
    t28 = $[112];
  }
  let t29;
  if ($[113] !== t26 || $[114] !== t28) {
    t29 = <button onClick={t26} className="w-full flex items-center justify-between p-3 rounded-lg bg-deep-blue hover:bg-tertiary-grey transition">{t27}{t28}</button>;
    $[113] = t26;
    $[114] = t28;
    $[115] = t29;
  } else {
    t29 = $[115];
  }
  let t30;
  if ($[116] !== showUpiId || $[117] !== upiId) {
    t30 = showUpiId && <div className="mt-3 p-3 bg-deep-blue rounded-lg"><input type="text" value={upiId} onChange={e_0 => setUpiId(e_0.target.value)} placeholder="yourname@bank" className="w-full bg-black border border-tertiary-grey rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary" /></div>;
    $[116] = showUpiId;
    $[117] = upiId;
    $[118] = t30;
  } else {
    t30 = $[118];
  }
  let t31;
  if ($[119] !== t25 || $[120] !== t29 || $[121] !== t30) {
    t31 = <div className="mb-4">{t23}{t25}{t29}{t30}</div>;
    $[119] = t25;
    $[120] = t29;
    $[121] = t30;
    $[122] = t31;
  } else {
    t31 = $[122];
  }
  let t32;
  if ($[123] !== isProcessing || $[124] !== showUpiId || $[125] !== upiId) {
    t32 = isProcessing || showUpiId && !upiId.trim();
    $[123] = isProcessing;
    $[124] = showUpiId;
    $[125] = upiId;
    $[126] = t32;
  } else {
    t32 = $[126];
  }
  const t33 = isProcessing ? "Processing..." : "Start Free Trial";
  let t34;
  if ($[127] !== handleProceedToPay || $[128] !== t32 || $[129] !== t33) {
    t34 = <button onClick={handleProceedToPay} disabled={t32} className="w-full bg-primary hover:bg-orange-dark text-black font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">{t33}</button>;
    $[127] = handleProceedToPay;
    $[128] = t32;
    $[129] = t33;
    $[130] = t34;
  } else {
    t34 = $[130];
  }
  let t35;
  if ($[131] !== t31 || $[132] !== t34) {
    t35 = <div className="p-4">{t31}{t34}</div>;
    $[131] = t31;
    $[132] = t34;
    $[133] = t35;
  } else {
    t35 = $[133];
  }
  let t36;
  if ($[134] !== t22 || $[135] !== t35) {
    t36 = <div className="w-full max-w-md bg-midnight-blue rounded-t-3xl text-white shadow-lg animate-in slide-in-from-bottom duration-300" onClick={_temp2}>{t22}{t35}</div>;
    $[134] = t22;
    $[135] = t35;
    $[136] = t36;
  } else {
    t36 = $[136];
  }
  let t37;
  if ($[137] !== handleClose || $[138] !== t36) {
    t37 = <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={handleClose}>{t36}</div>;
    $[137] = handleClose;
    $[138] = t36;
    $[139] = t37;
  } else {
    t37 = $[139];
  }
  return t37;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp(err) {
  console.error("Subscription payment start error", err);
}